import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "../components/Head";
import { fetchFullReport } from "../services/api";
import OBORY from "../constants/oborList.json";
import { SECTION_SLUGS } from "../constants/filterSlugs";
import { DATA_SOURCE_SHORT, DATA_UPDATED, DATA_UPDATED_LABEL, SECTION_NAMES, SITE_URL, STATS_PERIOD, publisherSchema } from "../content/site";
import NotFoundPage from "./NotFoundPage";
import "./style/content.css";

const nf = (d) => new Intl.NumberFormat("cs-CZ", { minimumFractionDigits: d, maximumFractionDigits: d });
const isNum = (v) => typeof v === "number" && Number.isFinite(v);
const pct = (v, d = 1) => (isNum(v) ? `${nf(d).format(v * 100)} %` : "—");
const num = (v, d = 2) => (isNum(v) ? nf(d).format(v) : "—");
const days = (v) => (isNum(v) ? `${nf(0).format(v)} dní` : "—");

// [název ve statistice, popisek, formát, nižší je lepší]
const METRICS = [
  ["EBITDA marže", "EBITDA marže", pct],
  ["Provozní marže", "Provozní marže", pct],
  ["ROA", "ROA (rentabilita aktiv)", pct],
  ["ROE", "ROE (rentabilita vlastního kapitálu)", pct],
  ["Likvidita běžná", "Běžná likvidita", num],
  ["Likvidita pohotová", "Pohotová likvidita", num],
  ["Zadluženost celková", "Celková zadluženost", pct],
  ["Obrat aktiv", "Doba obratu aktiv", days],
  ["Doba obratu pohledávek", "Doba obratu pohledávek", days],
  ["Doba obratu závazků", "Doba obratu závazků", days],
  ["Cyklus peněz", "Cyklus peněz", days],
  ["Marže přidané hodnoty", "Marže přidané hodnoty", pct],
  ["Osobní náklady / přidaná hodnota", "Osobní náklady / přidaná hodnota", pct],
  ["Meziroční růst tržeb", "Meziroční růst tržeb", pct],
];
const TREND = [["EBITDA marže", pct], ["ROA", pct], ["Zadluženost celková", pct], ["Likvidita běžná", num]];


export function IndustryIndexPage() {
  const url = `${SITE_URL}/obory`;
  const bySection = useMemo(() => {
    const m = {};
    OBORY.forEach((o) => { (m[o.sekce] = m[o.sekce] || []).push(o); });
    return m;
  }, []);
  return (
    <main className="content-page content-page--wide">
      <Helmet>
        <title>Finanční ukazatele podle oborů CZ-NACE | FinPilot</title>
        <meta name="description" content={`Mediány marží, rentability, likvidity, zadluženosti a růstu pro ${OBORY.length} oborů českých firem (oddíly CZ-NACE), období ${STATS_PERIOD}.`} />
        <meta name="robots" content="index,follow" />
        <meta property="og:title" content="Finanční ukazatele podle oborů CZ-NACE | FinPilot" />
        <meta property="og:url" content={url} />
        <link rel="canonical" href={url} />
      </Helmet>
      <span className="content-kicker">Přehled oborů</span>
      <h1>Finanční ukazatele českých firem podle oborů</h1>
      <p>
        Pro každý obor (oddíl CZ-NACE) najdete typické hodnoty marží, rentability, likvidity, zadluženosti, pracovního kapitálu
        a růstu, rozdělení podle velikosti firmy a vývoj po letech. Mediány za roky {STATS_PERIOD}, data aktualizována: {DATA_UPDATED_LABEL}.
      </p>
      {Object.keys(bySection).sort().map((s) => (
        <section key={s}>
          <h2>
            {s} – <Link to={`/oborove-statistiky/${SECTION_SLUGS[s] || ""}`}>{SECTION_NAMES[s] || s}</Link>
          </h2>
          <ul className="content-links">
            {bySection[s].map((o) => (
              <li key={o.slug}>
                <Link to={`/obor/${o.slug}`}>{o.code} {o.name}</Link> <span className="content-meta">({o.n} firem)</span>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </main>
  );
}

export function IndustryFactPage() {
  const { slug } = useParams();
  const obor = useMemo(() => OBORY.find((o) => o.slug === slug) || null, [slug]);
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!obor) return undefined;
    let alive = true;
    setData(null);
    setFailed(false);
    fetchFullReport("l1", obor.id)
      .then((r) => { if (alive) setData(r); })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, [obor]);

  if (!obor) return <NotFoundPage />;

  const row = data?.financial_ratios?.period_2023_2024?.[0] || null;
  const med = (m) => (row ? row[`${m}__median`] : null);
  const cagr = (start, metric) => (data?.growth_cagr || []).find((r) => r.window_start === start && r.metric === metric) || null;
  const g6 = cagr(2018, "trzby");
  const g3 = cagr(2022, "trzby");
  const sizes = [...(data?.by_size?.rows || [])].sort((a, b) => String(a.Velikost).localeCompare(String(b.Velikost)));
  const history = [...(data?.financial_ratios?.history || [])].filter((r) => /^20\d\d$/.test(String(r.Year))).sort((a, b) => a.Year - b.Year);

  const url = `${SITE_URL}/obor/${obor.slug}`;
  const title = `${obor.name} (CZ-NACE ${obor.code}): marže, rentabilita a zadluženost firem | FinPilot`;
  const lead = row
    ? `Typická firma v oboru ${obor.name} má EBITDA marži ${pct(med("EBITDA marže"))}, rentabilitu aktiv (ROA) ${pct(med("ROA"))}, běžnou likviditu ${num(med("Likvidita běžná"))} a celkovou zadluženost ${pct(med("Zadluženost celková"))}. Jde o mediány za roky ${STATS_PERIOD} z ${row.n} firem.`
    : `Finanční ukazatele firem v oboru ${obor.name} (CZ-NACE ${obor.code}): mediány marží, rentability, likvidity a zadluženosti za roky ${STATS_PERIOD}.`;
  const faq = row
    ? [
        [`Jaká je průměrná EBITDA marže v oboru ${obor.name}?`,
          `Medián EBITDA marže je ${pct(med("EBITDA marže"))}; polovina firem se pohybuje mezi ${pct(row["EBITDA marže__q1"])} a ${pct(row["EBITDA marže__q3"])} (roky ${STATS_PERIOD}, ${row.n} firem).`],
        [`Jak zadlužené jsou firmy v oboru ${obor.name}?`,
          `Medián celkové zadluženosti je ${pct(med("Zadluženost celková"))} a běžné likvidity ${num(med("Likvidita běžná"))}.`],
        [`Kolik firem v oboru ${obor.name} je ve ztrátě?`,
          `Ve ztrátě (záporný výsledek před zdaněním) je ${pct(row["Ztrátová firma__share"])} firem.`],
        ...(g6 ? [[`Jak rychle obor ${obor.name} roste?`,
          `Tržby typické firmy rostly v letech 2018–2024 o ${pct(g6.cagr_median)} ročně${g3 ? `, v letech 2022–2025 o ${pct(g3.cagr_median)} ročně` : ""}.`]] : []),
      ]
    : [];
  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Dataset",
        name: `Finanční ukazatele oboru ${obor.name} (CZ-NACE ${obor.code})`,
        description: lead,
        url,
        inLanguage: "cs-CZ",
        temporalCoverage: "2016/2025",
        dateModified: DATA_UPDATED,
        isAccessibleForFree: true,
        creator: publisherSchema,
        publisher: publisherSchema,
        variableMeasured: METRICS.map((m) => m[1]),
      },
      ...(faq.length ? [{
        "@type": "FAQPage",
        mainEntity: faq.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })),
      }] : []),
    ],
  };
  const siblings = OBORY.filter((o) => o.sekce === obor.sekce && o.slug !== obor.slug).slice(0, 12);

  return (
    <main className="content-page content-page--wide">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={lead.slice(0, 300)} />
        <meta name="robots" content="index,follow" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={lead.slice(0, 300)} />
        <meta property="og:url" content={url} />
        <link rel="canonical" href={url} />
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>
      <Link to="/obory" className="content-kicker">← Přehled oborů</Link>
      <h1>{obor.name}: finanční ukazatele firem</h1>
      <div className="content-meta">
        CZ-NACE {obor.code} · sekce {obor.sekce} {SECTION_NAMES[obor.sekce] || ""} · období {STATS_PERIOD} · data aktualizována: {DATA_UPDATED_LABEL}
      </div>
      <p className="content-lead">{lead}</p>

      {failed && <p>Data se nepodařilo načíst. Zkuste stránku obnovit.</p>}
      {!row && !failed && <p className="content-meta">Načítám data…</p>}

      {row && (
        <>
          <div className="content-stats">
            <div className="content-stat"><div className="content-stat__value">{pct(med("EBITDA marže"))}</div><div className="content-stat__label">EBITDA marže</div></div>
            <div className="content-stat"><div className="content-stat__value">{pct(med("ROA"))}</div><div className="content-stat__label">ROA</div></div>
            <div className="content-stat"><div className="content-stat__value">{num(med("Likvidita běžná"))}</div><div className="content-stat__label">běžná likvidita</div></div>
            <div className="content-stat"><div className="content-stat__value">{pct(med("Zadluženost celková"))}</div><div className="content-stat__label">zadluženost</div></div>
            <div className="content-stat"><div className="content-stat__value">{pct(row["Ztrátová firma__share"])}</div><div className="content-stat__label">firem ve ztrátě</div></div>
            <div className="content-stat"><div className="content-stat__value">{row.n}</div><div className="content-stat__label">firem ve vzorku</div></div>
          </div>

          <h2>Ukazatele oboru: medián a rozpětí</h2>
          <p>Polovina firem leží mezi dolním a horním kvartilem. Hodnoty jsou očištěné od extrémů.</p>
          <div className="content-table-wrap">
            <table>
              <thead><tr><th>Ukazatel</th><th className="num">Dolní kvartil</th><th className="num">Medián</th><th className="num">Horní kvartil</th></tr></thead>
              <tbody>
                {METRICS.filter(([m]) => isNum(med(m))).map(([m, label, f]) => (
                  <tr key={m}>
                    <td>{label}</td>
                    <td className="num">{f(row[`${m}__q1`])}</td>
                    <td className="num"><strong>{f(med(m))}</strong></td>
                    <td className="num">{f(row[`${m}__q3`])}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {(g6 || g3) && (
            <>
              <h2>Růst oboru</h2>
              <div className="content-table-wrap">
                <table>
                  <thead><tr><th>Období</th><th className="num">Růst tržeb typické firmy za rok</th><th className="num">Růst součtu tržeb oboru za rok</th><th className="num">Podíl rostoucích firem</th><th className="num">Firem</th></tr></thead>
                  <tbody>
                    {[["2018–2024", g6], ["2022–2025", g3]].filter(([, g]) => g).map(([label, g]) => (
                      <tr key={label}>
                        <td>{label}</td>
                        <td className="num"><strong>{pct(g.cagr_median)}</strong></td>
                        <td className="num">{pct(g.cagr_agg)}</td>
                        <td className="num">{pct(g.share_growing, 0)}</td>
                        <td className="num">{g.firms_eligible}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="content-meta">
                Typická firma = medián ročního růstu (CAGR) jednotlivých firem. Součet oboru = tržby všech firem dohromady; ve velmi
                koncentrovaných oborech ho určuje několik největších firem.
              </p>
            </>
          )}

          {sizes.length > 0 && (
            <>
              <h2>Podle velikosti firmy</h2>
              <div className="content-table-wrap">
                <table>
                  <thead><tr><th>Velikost (zaměstnanci)</th><th className="num">Firem</th><th className="num">EBITDA marže</th><th className="num">ROA</th><th className="num">Běžná likvidita</th><th className="num">Zadluženost</th><th className="num">Růst tržeb</th></tr></thead>
                  <tbody>
                    {sizes.map((s) => (
                      <tr key={s.Velikost}>
                        <td>{String(s.Velikost).slice(2)}{s.maly_vzorek === 1 ? " *" : ""}</td>
                        <td className="num">{s.n}</td>
                        <td className="num">{pct(s["EBITDA marže__median"])}</td>
                        <td className="num">{pct(s["ROA__median"])}</td>
                        <td className="num">{num(s["Likvidita běžná__median"])}</td>
                        <td className="num">{pct(s["Zadluženost celková__median"])}</td>
                        <td className="num">{pct(s["Meziroční růst tržeb__median"])}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {sizes.some((s) => s.maly_vzorek === 1) && <p className="content-meta">* méně než 20 firem – orientační hodnoty</p>}
            </>
          )}

          {history.length > 0 && (
            <>
              <h2>Vývoj po letech</h2>
              <div className="content-table-wrap">
                <table>
                  <thead><tr><th>Rok</th>{TREND.map(([m]) => <th key={m} className="num">{m}</th>)}<th className="num">Firem</th></tr></thead>
                  <tbody>
                    {history.map((h) => (
                      <tr key={h.Year}>
                        <td>{h.Year}</td>
                        {TREND.map(([m, f]) => <td key={m} className="num">{f(h[`${m}__median`])}</td>)}
                        <td className="num">{h.n}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          <h2>Časté otázky</h2>
          {faq.map(([q, a]) => (
            <div key={q}>
              <h3>{q}</h3>
              <p>{a}</p>
            </div>
          ))}
        </>
      )}

      <div className="content-cta">
        <p><strong>Jak si v tomto oboru stojí vaše firma?</strong> Nahrajte účetní závěrku a FinPilot ji porovná s oborem i s firmami podobné velikosti.</p>
        <Link className="content-btn" to="/financni-analyza">Analyzovat firmu</Link>
        <Link className="content-btn" to={`/oborove-statistiky/${SECTION_SLUGS[obor.sekce] || ""}`}>Interaktivní grafy sekce</Link>
      </div>

      <h2>Jak čísla vznikla</h2>
      <p>
        {DATA_SOURCE_SHORT} Do statistiky vstupují firmy s ročním obratem nad 30 milionů korun a aspoň třemi roky dat. Podrobnosti
        jsou na stránce <Link to="/o-projektu">O projektu a metodice</Link>.
      </p>

      {siblings.length > 0 && (
        <>
          <h2>Další obory v sekci {obor.sekce}</h2>
          <ul className="content-links">
            {siblings.map((o) => <li key={o.slug}><Link to={`/obor/${o.slug}`}>{o.code} {o.name}</Link></li>)}
          </ul>
        </>
      )}
    </main>
  );
}
